import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Network,
  ArrowRight,
  Sparkles,
  Globe,
  FileText,
  ExternalLink,
  ClipboardList,
  Loader2,
  TrendingUp,
  Trash2,
  X,
} from "lucide-react";

const ScoreRing = ({ score }) => {
  const color = score >= 70 ? "#C99734" : score >= 40 ? "#d97706" : "#6b7280";
  return (
    <div className="relative h-12 w-12 shrink-0">
      <svg viewBox="0 0 36 36" className="h-12 w-12 -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${score}, 100`} strokeLinecap="round" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-[Outfit] font-bold text-sm" style={{ color }}>{score}</span>
    </div>
  );
};

export default function Synergy() {
  const navigate = useNavigate();
  const [pairs, setPairs] = useState([]);
  const [opps, setOpps] = useState([]);
  const [analyzing, setAnalyzing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const loadOpps = () => api.get("/synergy/opportunities").then((r) => setOpps(r.data.opportunities)).catch(() => {});
  useEffect(() => {
    api.get("/synergy/candidates").then((r) => setPairs(r.data.pairs)).catch(() => {});
    loadOpps();
    api.post("/synergy/mark-seen").catch(() => {});
  }, []);

  const analyze = async (p) => {
    setAnalyzing(p.pair_id);
    try {
      const res = await api.post("/synergy/analyze", { source_client_id: p.source.client_id, target_client_id: p.target.client_id });
      toast.success("Opportunity brief generated");
      await loadOpps();
      setViewing(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(null);
    }
  };

  const removeOpp = async (id) => {
    try { await api.delete(`/synergy/opportunities/${id}`); loadOpps(); toast.success("Removed"); } catch { toast.error("Failed"); }
  };

  const dismissPair = async (pid) => {
    setPairs((ps) => ps.filter((p) => p.pair_id !== pid));
    try { await api.post(`/synergy/candidates/${pid}/dismiss`); } catch { toast.error("Nie udało się ukryć propozycji"); }
  };

  return (
    <div className="min-h-screen bg-[#F6F7F9]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2.5">
          <Network className="h-6 w-6 text-[#193962]" />
          <h1 className="font-[Outfit] font-bold tracking-tight text-4xl sm:text-5xl">Synergy & Business Matching</h1>
        </div>
        <p className="text-base text-muted-foreground font-[Manrope] mt-2">
          Agentic cross-border analysis across CRM data, Drive documents (RAG) and live web research.
        </p>

        {/* Candidate pairs */}
        <h2 className="mt-10 font-[Outfit] font-semibold text-lg">Cross-Border Candidates (Poland ↔ Kenya)</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="candidate-pairs">
          {pairs.map((p, i) => (
            <motion.div key={p.pair_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="border border-border bg-white rounded-md p-5" data-testid={`pair-${p.pair_id}`}>
              <div className="flex justify-end -mt-1 -mr-1 mb-1">
                <button onClick={() => dismissPair(p.pair_id)} data-testid={`dismiss-${p.pair_id}`} title="Ukryj tę propozycję"
                  className="h-7 w-7 flex items-center justify-center rounded hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-3 text-sm font-[Manrope]">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{p.source.company_name}</p>
                  <p className="text-xs text-muted-foreground">{p.source.region}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#193962] shrink-0" />
                <div className="flex-1 min-w-0 text-right">
                  <p className="font-medium truncate">{p.target.company_name}</p>
                  <p className="text-xs text-muted-foreground">{p.target.region}</p>
                </div>
              </div>
              <Button onClick={() => analyze(p)} disabled={analyzing === p.pair_id} data-testid={`analyze-${p.pair_id}`}
                className="mt-4 w-full bg-[#193962] hover:bg-[#12294A] text-white transition-all duration-200 hover:-translate-y-[1px]">
                {analyzing === p.pair_id ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Researching…</>) : (<><Sparkles className="h-4 w-4 mr-2" /> Generate Opportunity Brief</>)}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Generated opportunities */}
        <h2 className="mt-12 font-[Outfit] font-semibold text-lg">Synergy Opportunities</h2>
        <div className="mt-4 border border-border bg-white rounded-md divide-y divide-border" data-testid="opportunities-list">
          {opps.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground font-[Manrope]">No briefs yet. Generate one from a candidate pair above.</p>
          ) : opps.map((o) => (
            <div key={o.opportunity_id} className="px-5 py-4 flex items-center gap-4 hover:bg-zinc-50 transition-colors" data-testid={`opp-${o.opportunity_id}`}>
              <ScoreRing score={o.synergy_score} />
              <button onClick={() => setViewing(o)} data-testid={`view-opp-${o.opportunity_id}`} className="min-w-0 flex-1 text-left">
                <p className="font-[Manrope] font-medium text-sm truncate">{o.headline || `${o.source.company_name} × ${o.target.company_name}`}</p>
                <p className="text-xs text-muted-foreground font-[Manrope] truncate">{o.source.company_name} ({o.source.region}) × {o.target.company_name} ({o.target.region})</p>
              </button>
              {!o.web_research_live && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500 font-[Manrope]" title="Analiza oparta na danych CRM/AI — bez wyników live‑web dla tej pozycji">analiza AI</span>}
              <button onClick={() => removeOpp(o.opportunity_id)} className="h-8 w-8 flex items-center justify-center rounded hover:bg-red-50 text-zinc-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </main>

      <BriefDialog opp={viewing} onClose={() => setViewing(null)} navigate={navigate} />
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-2"><Icon className="h-4 w-4 text-[#193962]" /><h3 className="font-[Outfit] font-semibold tracking-tight">{title}</h3></div>
      {children}
    </section>
  );
}

function BriefDialog({ opp, onClose, navigate }) {
  if (!opp) return null;
  const ev = opp.sources_evidence || { drive_documents: [], web_sources: [] };
  const task = opp.exploration_task || {};
  return (
    <Dialog open={!!opp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="brief-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl tracking-tight">{opp.headline || "Opportunity Brief"}</DialogTitle>
          <DialogDescription className="font-[Manrope]">
            {opp.source.company_name} ({opp.source.region}) × {opp.target.company_name} ({opp.target.region}) · Synergy score {opp.synergy_score}/100
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Section icon={TrendingUp} title="Rationale" testid="brief-rationale">
            <p className="font-[Manrope] text-sm leading-relaxed text-zinc-700">{opp.rationale || "—"}</p>
          </Section>

          {opp.key_synergies?.length > 0 && (
            <Section icon={Sparkles} title="Key Synergies" testid="brief-synergies">
              <ul className="space-y-1.5">{opp.key_synergies.map((k, i) => (
                <li key={i} className="flex gap-2.5 font-[Manrope] text-sm text-zinc-700"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#193962] shrink-0" />{k}</li>
              ))}</ul>
            </Section>
          )}

          <Section icon={Globe} title="Market Validation (Live Web Research)" testid="brief-market-validation">
            <p className="font-[Manrope] text-sm leading-relaxed text-zinc-700">{opp.market_validation || "—"}</p>
          </Section>

          <Section icon={FileText} title="Sources & Evidence" testid="brief-sources">
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-zinc-400 font-[Manrope] mb-1.5">Google Drive documents</p>
                <div className="space-y-1.5">
                  {(ev.drive_documents || []).map((d, i) => (
                    <a key={i} href={d.url} target="_blank" rel="noreferrer" data-testid={`drive-source-${i}`} className="flex items-center gap-2 text-sm font-[Manrope] text-[#193962] hover:underline">
                      <FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{d.name}</span>
                    </a>
                  ))}
                  {(ev.drive_documents || []).length === 0 && <p className="text-sm text-muted-foreground font-[Manrope]">—</p>}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-zinc-400 font-[Manrope] mb-1.5">Web sources</p>
                <div className="space-y-1.5">
                  {(ev.web_sources || []).map((w, i) => (
                    <a key={i} href={w.url} target="_blank" rel="noreferrer" data-testid={`web-source-${i}`} className="flex items-center gap-2 text-sm font-[Manrope] text-[#193962] hover:underline">
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{w.title || w.url}</span>
                    </a>
                  ))}
                  {(ev.web_sources || []).length === 0 && <p className="text-sm text-muted-foreground font-[Manrope]">Brak źródeł live‑web dla tej analizy.</p>}
                </div>
              </div>
            </div>
          </Section>

          <Section icon={ClipboardList} title="Exploration Task" testid="brief-task">
            <div className="border border-border rounded-md p-4 bg-zinc-50">
              <p className="font-[Manrope] font-medium text-sm">{task.title || "—"}</p>
              <p className="font-[Manrope] text-sm text-zinc-700 whitespace-pre-wrap mt-1.5 leading-relaxed">{task.description || ""}</p>
              {task.assignee_role && <p className="text-xs text-[#193962] font-[Manrope] mt-2">Suggested owner: {task.assignee_role}</p>}
            </div>
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
